import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import swal from '@sweetalert/with-react';

import { useReportsActions } from "../../store/reportsStore";
import { useModalsActions } from "../../store/modalsStore";
import { CreateCourseMap } from "./CreateCourseMap";
import { useViewData } from "../../store/magnetboardStore";
import { userCanExportMagnetboardExcel } from "../utils/magnetboardExcelExport";

const SIS_DISPLAY_NAMES = {
  facts:"FACTS",
  genesis:"Genesis",
  gradelink:"Gradelink",
  infinite_campus:"Infinite Campus",
  powerschool:"PowerSchool",
  realtime:"Realtime",
  skyward:"Skyward",
};

const getSisDisplayName = (sis) => {
  const normalizedSis = `${sis || ""}`.trim().toLowerCase().replace(/\s+/g, "_");
  if(!normalizedSis) return "SIS";
  if(SIS_DISPLAY_NAMES[normalizedSis]) return SIS_DISPLAY_NAMES[normalizedSis];

  return normalizedSis
    .split("_")
    .filter(Boolean)
    .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ") || "SIS";
};

const MagnetboardMenu = React.memo(
  ({
    magnetboardOptions,
    scheduleData: { 
      scheduleInfo,
      schoolInfo,
      schoolSubscribed,
      courses,
      userInfo,
    },
    magnetboardActions: { 
      pullNewSectionData,
      duplicateSchedule,
      deleteSchedule,
      undoAction,
      redoAction,
      clearStudents,
      placeStudents,
      clearClassrooms,
      placeClassrooms,
      clearSections,
      placeSections,
      lockCurrentSections,
      unlockCurrentSections,
      requestPrintSchedule,
      requestExportCurrentView,
    },
  }) => {
    const { setShowExportScheduleModal } = useModalsActions();
    const { addOpenReport } = useReportsActions();

    const { currentView } = useViewData();
    const canExportCurrentView = userCanExportMagnetboardExcel(userInfo);
    const sisDisplayName = getSisDisplayName(scheduleInfo?.sis);

    const menu_ref = useRef();
    const [currentOpenMenu, setCurrentOpenMenu] = useState(null);
    const allowedReportSet = useMemo(() => {
      if(!Array.isArray(magnetboardOptions.allowed_reports)) return null;
      return new Set(magnetboardOptions.allowed_reports);
    }, [magnetboardOptions.allowed_reports]);
    const reportIsAllowed = useCallback((report) => (
      !allowedReportSet || allowedReportSet.has(report)
    ), [allowedReportSet]);
    const allowedActionGroupSet = useMemo(() => {
      if(!Array.isArray(magnetboardOptions.allowed_action_groups)) return null;
      return new Set(magnetboardOptions.allowed_action_groups);
    }, [magnetboardOptions.allowed_action_groups]);
    const actionGroupIsAllowed = useCallback((actionGroup) => (
      !allowedActionGroupSet || allowedActionGroupSet.has(actionGroup)
    ), [allowedActionGroupSet]);
    const allowedSectionActionSet = useMemo(() => {
      if(!Array.isArray(magnetboardOptions.allowed_section_actions)) return null;
      return new Set(magnetboardOptions.allowed_section_actions);
    }, [magnetboardOptions.allowed_section_actions]);
    const sectionActionIsAllowed = useCallback((sectionAction) => (
      !allowedSectionActionSet || allowedSectionActionSet.has(sectionAction)
    ), [allowedSectionActionSet]);

    const toggleMenuItem = (menu_item) => {
      let current_menu_item = null;
      if (menu_item !== currentOpenMenu) current_menu_item = menu_item;
      setCurrentOpenMenu(current_menu_item);
    };

    // Manual outside click - works correctly
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (menu_ref.current && !menu_ref.current.contains(event.target)) {
          setCurrentOpenMenu(null);
        }
      };

      if (currentOpenMenu) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [currentOpenMenu]);

    const handlePrint = useCallback(() => {
      setCurrentOpenMenu(null);
      if (typeof requestPrintSchedule === "function") requestPrintSchedule("schedule");
    }, [requestPrintSchedule]);

    const handlePrintByTeacher = useCallback(() => {
      setCurrentOpenMenu(null);
      if (typeof requestPrintSchedule === "function") requestPrintSchedule("by_teacher");
    }, [requestPrintSchedule]);

    const handleExportCurrentView = useCallback(() => {
      setCurrentOpenMenu(null);
      if (typeof requestExportCurrentView === "function") requestExportCurrentView();
    }, [requestExportCurrentView]);

    const loading_active_schedule = useMemo(() => 
      scheduleInfo.app_current_schedule_year_id !== undefined && 
      schoolInfo.current_schedule_year_id !== undefined && 
      scheduleInfo.app_current_schedule_year_id === schoolInfo.current_schedule_year_id
      , [scheduleInfo, schoolInfo]);

    const current_schedule_year_schedule_count = useMemo(() => {
      if(!scheduleInfo?.schedules || !schoolInfo?.current_schedule_year_id) return 0;

      return scheduleInfo.schedules.filter(schedule => `${schedule.schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`).length;
    }, [scheduleInfo, schoolInfo]);

    const current_schedule_year_info = useMemo(() => {
      if(!scheduleInfo?.schedule_years || !schoolInfo?.current_schedule_year_id) return null;

      return scheduleInfo.schedule_years.find(schedule_year => `${schedule_year.schedule_year_id}` === `${schoolInfo.current_schedule_year_id}`) || null;
    }, [scheduleInfo, schoolInfo]);

    const max_schedules_allowed = useMemo(() => {
      if(!current_schedule_year_info?.num_schedules_allowed) return 5;

      return parseInt(current_schedule_year_info.num_schedules_allowed, 10);
    }, [current_schedule_year_info]);

    const duplicate_schedule_limit_reached = current_schedule_year_schedule_count >= max_schedules_allowed;

    // Handle Dropdown Hovering 
    const handleDropdownHoverShow = (action_type) => {
      // Hide all other submenus
      const all_submenus = document.querySelectorAll('.mb-dropdown-options-subcontainer');
      for(var i = 0; i < all_submenus.length; i++)
      {
        all_submenus[i].classList.add('hide');
      }

      // Show the current submenu
      const parent_menu = document.querySelector('.mb-dropdown-option-hover[data-actiontype="' + action_type + '"]');
      const parent_menu_top_position = parent_menu.offsetTop + 42;

      const submenu = document.querySelector('.mb-dropdown-options-subcontainer[data-actiontype="' + action_type + '"]');
      if(submenu) 
      {
        submenu.style.top = parent_menu_top_position + "px";
        submenu.classList.remove("hide");
      }
    }

    const handleDropdownHoverHide = (action_type) => {
      const submenu = document.querySelector('.mb-dropdown-options-subcontainer[data-actiontype="' + action_type + '"]');

      if(submenu) submenu.classList.add("hide");
    }

    const createHealthSections = async () => {	
      const options =  {
        title: "Auto Create Health Sections",
        text: 'This will automatically create health sections for all your Phys Ed/Gym sections.',
        buttons:false,
        content: (
          <CreateCourseMap 
            scheduleData={{ courses, schoolInfo }}
            magnetboardActions={{ pullNewSectionData }}
          />
        )
      }
      
      swal(options);
    }

    const confirmDeleteSchedule = async () => {
      const options = {
        title: "Are you sure?",
        text: "This will delete this schedule version. This action cannot be undone.",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            text: "Cancel",
            value: false,
            visible: true,
            className: 'gray-btn'
          },
          confirm: {
            text: "Delete Schedule",
            value: true,
            visible: true,
            className: 'red-btn',
            closeModal: false
          },
        },
      };

      const confirmed = await swal(options);
      if(!confirmed) return;

      setCurrentOpenMenu(null);
      try {
        await Promise.resolve(deleteSchedule());
      } finally {
        swal.close();
      }
    }

    return (
      <div className="mb-menu-bar" ref={menu_ref}>
        <div className="mb-menu-btn" onClick={() => toggleMenuItem("file")}>
          <span>File</span>
          {currentOpenMenu === "file" && (
            <div className="mb-menu-dropdown-options-container">
              {/* {loading_active_schedule &&
                <div className='mb-dropdown-option' onClick={() => saveSchedule('new')}>Save As..</div>
              } */}
              <div className="mb-dropdown-option" onClick={handlePrint}>
                Print Schedule
              </div>
              {false && currentView === "teacher" && (
                <div className="mb-dropdown-option" onClick={handlePrintByTeacher}>
                  Print Per Teacher
                </div>
              )}
              {loading_active_schedule && (
                !magnetboardOptions.hide_duplicate_schedule && <div className={`mb-dropdown-option ${duplicate_schedule_limit_reached ? 'mb-dropdown-option-muted' : ''}`} title={duplicate_schedule_limit_reached ? `Maximum of ${max_schedules_allowed} schedules reached for this year` : 'Duplicate schedule'} onClick={() => {
                  setCurrentOpenMenu(null);
                  duplicateSchedule();
                }}>
                  Duplicate Schedule
                </div>
              )}
              {(loading_active_schedule && current_schedule_year_schedule_count > 1 && !magnetboardOptions.hide_delete_schedule) && (
                <div className="mb-dropdown-option" onClick={confirmDeleteSchedule}>
                  Delete Schedule
                </div>
              )}
              {canExportCurrentView && !magnetboardOptions.hide_export_schedule && (
                <div className="mb-dropdown-option" onClick={handleExportCurrentView}>
                  Export to Excel
                </div>
              )}
              {loading_active_schedule && !magnetboardOptions.hide_export_schedule && (
                <div
                  className={`mb-dropdown-option ${
                    !schoolSubscribed && "gray-text"
                  }`}
                  onClick={
                    schoolSubscribed
                      ? () => setShowExportScheduleModal(true)
                      : () =>
                          alert(
                            "Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!"
                          )
                  }
                >
                  Export to {sisDisplayName}
                </div>
              )}
            </div>
          )}
        </div>
        {loading_active_schedule && (
          <>
            {!magnetboardOptions.hide_edit && <div className="mb-menu-btn" onClick={() => toggleMenuItem("edit")}>
              <span>Edit</span>
              {currentOpenMenu === "edit" && (
                <div className="mb-menu-dropdown-options-container">
                  <div className="mb-dropdown-option" onClick={undoAction}>
                    <div>Undo</div>
                    <div className="small-text dark-gray-text text-align-right">
                      Ctrl + Z
                    </div>
                  </div>
                  <div className="mb-dropdown-option" onClick={redoAction}>
                    <div>Redo</div>
                    <div className="small-text dark-gray-text text-align-right">
                      Ctrl + Y
                    </div>
                  </div>
                </div>
              )}
            </div>}
            {!magnetboardOptions.hide_actions && <div
              className="mb-menu-btn"
              onClick={() => toggleMenuItem("actions")}
            >
              <span>Actions</span>
              {currentOpenMenu === "actions" && (
                <div className="mb-menu-dropdown-options-container">
                  {actionGroupIsAllowed("students") && (
                    <div
                      className="mb-dropdown-option mb-dropdown-option-hover"
                      data-actiontype="students"
                      onMouseEnter={() => handleDropdownHoverShow("students")}
                      onMouseLeave={() => handleDropdownHoverHide("students")}
                    >
                      <div>Students</div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  )}
                  {actionGroupIsAllowed("sections") && (
                    <div
                      className="mb-dropdown-option mb-dropdown-option-hover"
                      data-actiontype="sections"
                      onMouseEnter={() => handleDropdownHoverShow("sections")}
                      onMouseLeave={() => handleDropdownHoverHide("sections")}
                    >
                      <div>Sections</div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  )}
                  {actionGroupIsAllowed("classrooms") && (
                    <div
                      className="mb-dropdown-option mb-dropdown-option-hover"
                      data-actiontype="classrooms"
                      onMouseEnter={() => handleDropdownHoverShow("classrooms")}
                      onMouseLeave={() => handleDropdownHoverHide("classrooms")}
                    >
                      <div>Classrooms</div>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                  )}
                  {actionGroupIsAllowed("health_sections") && (
                    <div
                      className={`mb-dropdown-option ${
                        !schoolSubscribed && "gray-text"
                      }`}
                      onClick={
                        schoolSubscribed
                          ? createHealthSections
                          : () =>
                              alert(
                                "Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!"
                              )
                      }
                    >
                      Create Health Sections
                    </div>
                  )}
                </div>
              )}
              {actionGroupIsAllowed("students") && (
                <div
                  className="mb-dropdown-options-subcontainer hide"
                  data-actiontype="students"
                  onMouseEnter={() => handleDropdownHoverShow("students")}
                  onMouseLeave={() => handleDropdownHoverHide("students")}
                >
                  <div className="mb-dropdown-option" onClick={clearStudents}>
                    Clear Students
                  </div>
                  <div
                    className={`mb-dropdown-option ${
                      !schoolSubscribed && "gray-text"
                    }`}
                    onClick={
                      schoolSubscribed
                        ? placeStudents
                        : () =>
                            alert(
                              "Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!"
                            )
                    }
                  >
                    Place Students
                  </div>
                </div>
              )}
              {actionGroupIsAllowed("classrooms") && (
                <div
                  className="mb-dropdown-options-subcontainer hide"
                  data-actiontype="classrooms"
                  onMouseEnter={() => handleDropdownHoverShow("classrooms")}
                  onMouseLeave={() => handleDropdownHoverHide("classrooms")}
                >
                  <div className="mb-dropdown-option" onClick={clearClassrooms}>
                    Clear Classrooms
                  </div>
                  <div
                    className={`mb-dropdown-option ${
                      !schoolSubscribed && "gray-text"
                    }`}
                    onClick={
                      schoolSubscribed
                        ? placeClassrooms
                        : () =>
                            alert(
                              "Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!"
                            )
                    }
                  >
                    Place Classrooms
                  </div>
                </div>
              )}
              {actionGroupIsAllowed("sections") && (
                <div
                  className="mb-dropdown-options-subcontainer hide"
                  data-actiontype="sections"
                  onMouseEnter={() => handleDropdownHoverShow("sections")}
                  onMouseLeave={() => handleDropdownHoverHide("sections")}
                >
                  {sectionActionIsAllowed("clear") && (
                    <div className="mb-dropdown-option" onClick={clearSections}>
                      Clear Sections
                    </div>
                  )}
                  {sectionActionIsAllowed("place") && (
                    <div
                      className={`mb-dropdown-option ${
                        !schoolSubscribed && "gray-text"
                      }`}
                      onClick={
                        schoolSubscribed
                          ? placeSections
                          : () =>
                              alert(
                                "Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!"
                              )
                      }
                    >
                      Place Sections
                    </div>
                  )}
                  {(currentView === "teacher" || currentView === "course" || currentView === "classroom") && (
                    <>
                      {sectionActionIsAllowed("unlock") && (
                        <div
                          className="mb-dropdown-option"
                          onClick={unlockCurrentSections}
                        >
                          Unlock Sections
                        </div>
                      )}
                      {sectionActionIsAllowed("lock") && (
                        <div
                          className="mb-dropdown-option"
                          onClick={lockCurrentSections}
                        >
                          Lock Sections
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>}
          </>
        )}
        {!magnetboardOptions.hide_reports && <div className="mb-menu-btn" onClick={() => toggleMenuItem("reports")}>
          <span>Reports</span>
          {currentOpenMenu === "reports" && (
            <div className="mb-menu-dropdown-options-container">
              {reportIsAllowed("schedule-summary") && (
                <div
                  className="mb-dropdown-option"
                  onClick={() => addOpenReport("schedule-summary")}
                >
                  Schedule Summary
                </div>
              )}
              {reportIsAllowed("student-summary") && (
                <div
                  className="mb-dropdown-option"
                  onClick={() => addOpenReport("student-summary")}
                >
                  Student Summary
                </div>
              )}
              {reportIsAllowed("conflicts") && (
                <div
                  className="mb-dropdown-option"
                  onClick={() => addOpenReport("conflicts")}
                >
                  Conflicts/Warnings
                </div>
              )}
              {reportIsAllowed("section-balancing") && (
                <div
                  className="mb-dropdown-option"
                  onClick={() => addOpenReport("section-balancing")}
                >
                  Balance Report
                </div>
              )}
              {reportIsAllowed("conflict-matrix") && (
                <div
                  className="mb-dropdown-option"
                  onClick={() => addOpenReport("conflict-matrix")}
                >
                  Conflict Matrix
                </div>
              )}
            </div>
          )}
        </div>}
        {/* <div className="mb-menu-btn"><span>Help</span></div> */}
      </div>
    );
  }
);

export default MagnetboardMenu;
